import React from "react";

import { Field, Form } from "react-final-form";
import formatCurrency from "Utils/formatCurrency";
import { LifePlan, Plan, SegmentPlanCost } from "Utils/types";
import { validateRequired } from "Utils/Validations";

import BodyText, { Body2 } from "Cobalt/BodyText";
import Header from "Cobalt/Header";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalProps } from "Cobalt/Modal";
import { Radio } from "Cobalt/RadioGroup";
import { HeaderTR, Table, TD, TH, TR } from "Cobalt/TableV2";
import CarrierLogo from "Common/CarrierLogo";
import { SaveButton } from "Common/forms/buttons";

export interface SegmentEnrollmentFormSubmitValues {
  selectedSegment: "ee_only" | "ee_spouse" | "ee_children" | "ee_family" | "none";
}

export interface SegmentEnrollmentFormValueProps {
  selectedSegment: "ee_only" | "ee_spouse" | "ee_children" | "ee_family" | "none" | null | undefined;
}

interface SegmentEnrollmentModalProps extends ModalProps {
  plan: Plan | LifePlan;
  planCost: SegmentPlanCost;
  title: string;
  initialValues: SegmentEnrollmentFormValueProps;
  onSubmit: (values: SegmentEnrollmentFormSubmitValues) => void;
}

const SegmentRow = ({ segment, label, cost }) => (
  <TR highlightFirstCell={false} withBorder={false}>
    <TD>
      <Field name="selectedSegment" type="radio" value={segment} key={segment} validate={validateRequired}>
        {({ input }) => (
          <Radio name={input.name} onChange={input.onChange} value={input.value} checked={input.checked} inline>
            {label}
          </Radio>
        )}
      </Field>
    </TD>
    <TD>{formatCurrency(cost)}</TD>
  </TR>
);

const SegmentEnrollmentModal = ({ plan, planCost, title, initialValues, onSubmit, ...rest }: SegmentEnrollmentModalProps) => {
  const { eeOnly, eeSpouse, eeChildren, eeFamily } = planCost.costBreakdowns;
  return (
    <Modal {...rest}>
      <Form initialValues={initialValues} onSubmit={onSubmit}>
        {({ handleSubmit, submitting, touched, errors }) => (
          <form onSubmit={handleSubmit}>
            <ModalHeader closeButton>
              <Header level={6} mb={0}>
                {title}
              </Header>
            </ModalHeader>
            <ModalBody>
              <BodyText className="flex items-end" mb={4}>
                <CarrierLogo plan={plan} maxSize={3} className="flex-grow" mr={3} />
                <span className="flex-grow">{plan.display_name}</span>
              </BodyText>
              <Table>
                <thead>
                  <HeaderTR>
                    <TH>Who is covered</TH>
                    <TH width="60px" align="left">
                      My Monthly Cost
                    </TH>
                  </HeaderTR>
                </thead>
                <tbody>
                  <SegmentRow segment="ee_only" label="Just Me" cost={eeOnly.employeeTotalCost} />
                  {eeSpouse && <SegmentRow segment="ee_spouse" label="Me and My Spouse" cost={eeSpouse.employeeTotalCost} />}
                  {eeChildren && <SegmentRow segment="ee_children" label="Me and My Children" cost={eeChildren.employeeTotalCost} />}
                  {eeFamily && <SegmentRow segment="ee_family" label="Me, My Spouse, and My Children" cost={eeFamily.employeeTotalCost} />}
                  <SegmentRow segment="none" label="Decline All Coverage" cost={0} />
                </tbody>
              </Table>
            </ModalBody>
            <ModalFooter className="flex justify-between items-center">
              <Body2 mb={3} color="error">
                {touched?.selectedSegment && errors?.selectedSegment ? "Please select who is enrolling first." : <>&nbsp;</>}
              </Body2>
              <SaveButton submitting={submitting} />
            </ModalFooter>
          </form>
        )}
      </Form>
    </Modal>
  );
};

export default SegmentEnrollmentModal;
